import type { SelectProps } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import type { ReactNode } from 'react';

type LabeledOption = DefaultOptionType & {
  label?: ReactNode;
  searchTokens?: string;
};

function optionLabelText(option?: LabeledOption): string {
  if (!option) return '';
  if (typeof option.searchTokens === 'string') return option.searchTokens;
  const { label } = option;
  if (typeof label === 'string') return label;
  if (typeof label === 'number') return String(label);
  return '';
}

/** Filter Select options by `label` / `searchTokens` (Ant Design v6 `options` API). */
export function filterSelectOption(
  input: string,
  option?: LabeledOption,
): boolean {
  const haystack = optionLabelText(option).toLowerCase();
  return haystack.includes(input.toLowerCase());
}

type ShowSearchConfig = Extract<SelectProps['showSearch'], object>;

export function filterShowSearch(
  filterOption: ShowSearchConfig['filterOption'],
): NonNullable<SelectProps['showSearch']> {
  return { optionFilterProp: 'label', filterOption };
}

/** Common Select search config — filters by option label / searchTokens. */
export const defaultFilterShowSearch: NonNullable<SelectProps['showSearch']> =
  filterShowSearch(filterSelectOption);

function isMultiValueSelectMode(mode: SelectProps['mode']): boolean {
  return mode === 'multiple' || mode === 'tags';
}

/** Default showSearch for multiple/tags selects when caller did not set one. */
export function defaultShowSearchForSelectMode(
  mode: SelectProps['mode'],
  showSearch: SelectProps['showSearch'],
): SelectProps['showSearch'] {
  if (showSearch != null) return showSearch;
  if (isMultiValueSelectMode(mode)) return defaultFilterShowSearch;
  return undefined;
}

export function searchShowSearch(
  onSearch: ShowSearchConfig['onSearch'],
): SelectProps['showSearch'] {
  return { filterOption: false, onSearch };
}

export function withEmptyOption(
  options: DefaultOptionType[],
  emptyLabel: ReactNode,
  emptyValue: string | number = '',
): DefaultOptionType[] {
  return [{ value: emptyValue, label: emptyLabel }, ...options];
}
